import { useMutation } from '@tanstack/react-query';
import { ValidationError } from 'yup';
import {
  TransferPaymentDepositPayload,
  finalTransferPaymentDepositSchema,
} from '../../types/appointments';
import useUser from '../global/useUser';
import { transferPaymentDeposit } from '../../services/appointments';

interface Params {
  onRefresh?: () => void | Promise<void>;
  onSuccess?: (
    result: Awaited<ReturnType<typeof transferPaymentDeposit>>,
  ) => void;
  onError?: (error: string) => void;
}

function useTransferPaymentDeposit({ onRefresh, onSuccess, onError }: Params) {
  const { user } = useUser();

  return useMutation({
    mutationFn: async (
      payload: Omit<TransferPaymentDepositPayload, 'businessId'>,
    ) => {
      if (!user?.business?.id) throw new Error('Invalid business');
      const validatedPayload = await finalTransferPaymentDepositSchema
        .noUnknown()
        .validate(
          { ...payload, businessId: user?.business?.id },
          {
            stripUnknown: true,
          },
        );

      const result = await transferPaymentDeposit(validatedPayload);

      try {
        await onRefresh?.();
      } catch (error) {
        // Empty
      }

      return result;
    },
    onSuccess,
    onError: error => {
      if (error instanceof ValidationError) {
        onError?.(error.message);
      } else {
        onError?.('Failed to transfer deposit');
      }
    },
  });
}

export default useTransferPaymentDeposit;
